import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import formOptions from "../config/formOptions.json";

import NullableBooleanSelect from "./NullableBooleanSelect";

import {
  DOCUMENTED_STATUSES,
  PROCESS_STATUS,
  createEmptyResponse,
} from "../utils/operationalAuditResponses";

const AuditQuestionCard = ({
  question,
  response,
  auditLocked,
  onUpdate,
  onProcessStatusChange,
  onDocumentationStatusChange,
}) => {
  const currentResponse = {
    ...createEmptyResponse(),
    ...(response || {}),
  };

  const processFrequencyOptions =
  formOptions.processFrequencyOptions ?? [];

if (processFrequencyOptions.length === 0) {
  console.warn(
    "No processFrequencyOptions found in formOptions.json",
  );
}

  const hasNoDefinedProcess =
    currentResponse.processStatus ===
    PROCESS_STATUS.NO_DEFINED_PROCESS;

  const hasDefinedProcess =
    currentResponse.processStatus ===
    PROCESS_STATUS.DEFINED_PROCESS_EXISTS;

  const isApplicable =
    hasNoDefinedProcess ||
    hasDefinedProcess;

  const isDocumented =
    hasDefinedProcess &&
    DOCUMENTED_STATUSES.includes(
      currentResponse.documentationStatus,
    );

  return (
    <Box
      p={2}
      border="1px solid"
      borderColor="divider"
      borderRadius="8px"
    >
      <Box mb={2}>
        <Typography
          variant="h5"
          fontWeight={700}
          color="var(--tenant-primary)"
          sx={{
            mb: question.description ? 1 : 0,
          }}
        >
          {question.statement}
        </Typography>

        {question.description && (
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {question.description}
          </Typography>
        )}
      </Box>

      <Box
        display="grid"
        gap={2}
        gridTemplateColumns={{
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
        }}
      >
        <FormControl
          fullWidth
          disabled={auditLocked}
          sx={{
            gridColumn: {
              xs: "span 1",
              sm: "span 2",
              lg: "span 2",
            },
          }}
        >
          <InputLabel>
            Activity Status
          </InputLabel>

          <Select
            value={
              currentResponse.processStatus
            }
            label="Activity Status"
            onChange={(event) =>
              onProcessStatusChange(
                event.target.value,
              )
            }
          >
            <MenuItem value="">
              Not assessed
            </MenuItem>

            <MenuItem
              value={
                PROCESS_STATUS.NOT_APPLICABLE
              }
            >
              Does not apply
            </MenuItem>

            <MenuItem
              value={
                PROCESS_STATUS.NO_DEFINED_PROCESS
              }
            >
              Applies, but no defined process exists
            </MenuItem>

            <MenuItem
              value={
                PROCESS_STATUS.DEFINED_PROCESS_EXISTS
              }
            >
              A defined process exists
            </MenuItem>
          </Select>
        </FormControl>

        {isApplicable && (
          <>
            <FormControl
              fullWidth
              disabled={auditLocked}
            >
              <InputLabel>
                Frequency
              </InputLabel>

              <Select
              value={currentResponse.processFrequency}
              label="Frequency"
              onChange={(event) =>
                onUpdate({
                  processFrequency: event.target.value,
                })
              }
            >
              <MenuItem value="">
                Not assessed
              </MenuItem>

              {processFrequencyOptions.length === 0 && (
                <MenuItem disabled>
                  No frequency options configured
                </MenuItem>
              )}

              {processFrequencyOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            </FormControl>

            <FormControl
              fullWidth
              disabled={auditLocked}
            >
              <InputLabel>
                Business Impact
              </InputLabel>

              <Select
                value={
                  currentResponse.businessImpact
                }
                label="Business Impact"
                onChange={(event) =>
                  onUpdate({
                    businessImpact:
                      event.target.value,
                  })
                }
              >
                <MenuItem value="">
                  Not assessed
                </MenuItem>

                {formOptions.impactOptions.map(
                  (option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <NullableBooleanSelect
              label="Has owner"
              value={currentResponse.hasOwner}
              disabled={auditLocked}
              onChange={(value) =>
                onUpdate({
                  hasOwner: value,

                  ...(value !== true
                    ? {
                        ownerName: "",
                      }
                    : {}),
                })
              }
            />

            <NullableBooleanSelect
              label="Known by one person"
              value={
                currentResponse.knownByOnePerson
              }
              disabled={auditLocked}
              onChange={(value) =>
                onUpdate({
                  knownByOnePerson: value,
                })
              }
            />

            {currentResponse.hasOwner ===
              true && (
              <TextField
                fullWidth
                disabled={auditLocked}
                label="Owner name"
                value={
                  currentResponse.ownerName
                }
                onChange={(event) =>
                  onUpdate({
                    ownerName:
                      event.target.value,
                  })
                }
              />
            )}

            <TextField
              fullWidth
              disabled={auditLocked}
              label="Known by people"
              placeholder="Jane, Dave, Sarah"
              value={
                currentResponse.knownByPeopleText
              }
              onChange={(event) =>
                onUpdate({
                  knownByPeopleText:
                    event.target.value,
                })
              }
              sx={{
                gridColumn: {
                  xs: "span 1",
                  sm: "span 2",
                },
              }}
            />
          </>
        )}

        {hasDefinedProcess && (
          <>
            <FormControl
              fullWidth
              disabled={auditLocked}
            >
              <InputLabel>
                Documentation
              </InputLabel>

              <Select
                value={
                  currentResponse.documentationStatus
                }
                label="Documentation"
                onChange={(event) =>
                  onDocumentationStatusChange(
                    event.target.value,
                  )
                }
              >
                <MenuItem value="">
                  Not assessed
                </MenuItem>

                {formOptions.documentationOptions.map(
                  (option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              disabled={auditLocked}
            >
              <InputLabel>
                Confidence
              </InputLabel>

              <Select
                value={
                  currentResponse.confidenceLevel
                }
                label="Confidence"
                onChange={(event) =>
                  onUpdate({
                    confidenceLevel:
                      event.target.value,
                  })
                }
              >
                <MenuItem value="">
                  Not assessed
                </MenuItem>

                {formOptions.confidenceOptions.map(
                  (option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </>
        )}

        {isDocumented && (
          <>
            <FormControl
              fullWidth
              disabled={auditLocked}
            >
              <InputLabel>
                Storage
              </InputLabel>

              <Select
                value={
                  currentResponse.storageType
                }
                label="Storage"
                onChange={(event) =>
                  onUpdate({
                    storageType:
                      event.target.value,
                  })
                }
              >
                <MenuItem value="">
                  Not assessed
                </MenuItem>

                {formOptions.storageOptions.map(
                  (option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <NullableBooleanSelect
              label="Trusted"
              value={currentResponse.isTrusted}
              disabled={auditLocked}
              onChange={(value) =>
                onUpdate({
                  isTrusted: value,
                })
              }
            />

            <NullableBooleanSelect
              label="In use"
              value={currentResponse.isInUse}
              disabled={auditLocked}
              onChange={(value) =>
                onUpdate({
                  isInUse: value,
                })
              }
            />

            <NullableBooleanSelect
              label="Reviewed regularly"
              value={
                currentResponse.isReviewedRegularly
              }
              disabled={auditLocked}
              onChange={(value) =>
                onUpdate({
                  isReviewedRegularly: value,
                })
              }
            />

            <NullableBooleanSelect
              label="Staff trained"
              value={
                currentResponse.staffTrained
              }
              disabled={auditLocked}
              onChange={(value) =>
                onUpdate({
                  staffTrained: value,
                })
              }
            />
          </>
        )}

        {currentResponse.processStatus && (
          <TextField
            fullWidth
            disabled={auditLocked}
            label="Notes"
            value={currentResponse.notes}
            onChange={(event) =>
              onUpdate({
                notes: event.target.value,
              })
            }
            multiline
            minRows={2}
            sx={{
              gridColumn: {
                xs: "span 1",
                sm: "span 2",
                lg: "span 4",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default AuditQuestionCard;